import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ChipInput } from '@parsys/frontend_library';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import {
  updateIsPatientNotifEnabled,
  updateIsThirdPartyNotifEnabled,
  updateReason,
  updateThirdPartyEmailList,
} from 'FRONTEND/reducers/bookingSlice';
import { selectManagedExpertById } from 'FRONTEND/reducers/expertSlice';
import { expectEdition, selectPatientById } from 'FRONTEND/reducers/patientSlice';
import { FormTypes } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getPatientUpdatePath } from 'FRONTEND/utils/routes';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

export const NotifCheckboxReasonStep = ({
  noCheckbox = false,
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();

  // Payment conditions
  const appointmentHasPayment = useSelector((state) => state.booking?.hasPayment);

  // Main email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic validation
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (shouldReset) {
      dispatch(updateReason({ reason: null }));
      dispatch(updateIsThirdPartyNotifEnabled({ isThirdPartyNotifEnabled: false }));
      dispatch(updateThirdPartyEmailList({ emailList: [] }));
    }
  }, [shouldReset]);

  // Submit management
  const selectedPatient = useSelector((state) => selectPatientById(state, state.booking.selectedPatientId));
  const reason = useSelector((state) => state.booking.reason) || '';
  const isPatientNotifEnabled = useSelector((state) => state.booking.isPatientNotifEnabled);
  const isUnrestrictedMode = useSelector((state) => state.calendar.isUnrestrictedMode);
  const isPatientForm = useSelector((state) => state.booking.formType === FormTypes.PATIENT_FORM);

  // Premiocare validation
  const formType = useSelector((state) => state.booking.formType);
  const tenantList = useSelector((state) => state.user.tenantList);
  const selectedLocation = useSelector((state) => state.booking.selectedLocationId ? state.location.entities[state.booking.selectedLocationId] : null);
  const selectedExpert = useSelector((state) => selectManagedExpertById(state, state.booking.selectedExpertId));
  const isLocationFormPremiocareTenant = formType === FormTypes.LOCATION_FORM &&
    Boolean(selectedLocation?.tenantId && tenantList[selectedLocation.tenantId]?.premiocare_cabinet_id);
  const PREMIOCARE_MANDATORY_PATIENT_KEYS = ['firstname', 'lastname', 'email', 'gender', 'birthdate', 'birthplaceInsee'];
  const isFieldPresent = (value) => Boolean(value) && value !== 'None';
  const hasPatientMissingFields = isLocationFormPremiocareTenant && selectedPatient ? PREMIOCARE_MANDATORY_PATIENT_KEYS.some((key) => !isFieldPresent(selectedPatient[key])) : false;
  const expertMissingRpps = isLocationFormPremiocareTenant && selectedExpert && !selectedExpert.prNumber;

  const handlePatientNotifChange = (newIsPatientNotifEnabled) => {
    dispatch(updateIsPatientNotifEnabled({ isEnabled: newIsPatientNotifEnabled }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onStepCompleted();
  };

  const handleAddContactInfoClick = () => {
    dispatch(expectEdition());
    $.pjax({ url: getPatientUpdatePath(selectedPatient.logicalId), container: PJAX_MAIN_CONTAINER_SELECTOR });
  };

  let displayName = `${selectedPatient?.firstname ? `${selectedPatient?.firstname} ` : ''}${selectedPatient?.lastname ? `${selectedPatient?.lastname} ` : ''}`;
  displayName = displayName || i18n._('Anonymous patient');

  const patientNotificationString = isValidEmail(selectedPatient?.email) && (noCheckbox || isPatientNotifEnabled) ? (
    `${displayName} ${i18n._('will receive an invitation and a reminder by email.')} ${appointmentHasPayment ? i18n._('A link to the payment page will also be sent') : ''}`
  ) : (
    `${displayName} ${i18n._('will not receive an invitation nor a reminder by email.')}`
  );

  // Third party notification management
  const isThirdPartyNotifEnabled = useSelector((state) => state.booking.isThirdPartyNotifEnabled);
  const handleThirdPartyCheckBoxChanged = (newIsThirdPartyNotifEnabled) => {
    dispatch(updateIsThirdPartyNotifEnabled({ isThirdPartyNotifEnabled: newIsThirdPartyNotifEnabled }));
  };
  const [emailErrorText, setEmailErrorText] = useState('');
  const emailInputRef = useRef();
  const validateEmail = () => {
    if (emailInputRef.current.checkValidity()) {
      return true;
    }
    setEmailErrorText(i18n._('Please enter a valid email address.'));
    return false;
  };
  const thirdPartyEmailList = useSelector((state) => state.booking.thirdPartyEmailList);
  const handleThirdPartyEmailListChange = (newEmailList) => {
    if (newEmailList.length > thirdPartyEmailList.length) {
      setThirdPartyEmailAddressValue('');
    }
    dispatch(updateThirdPartyEmailList({ emailList: newEmailList }));
  };
  const [thirdPartyEmailAddressValue, setThirdPartyEmailAddressValue] = useState('');
  const handleThirdPartyEmailInputChange = (event) => {
    setEmailErrorText('');
    setThirdPartyEmailAddressValue(event?.target?.value ?? '');
  };

  const getAlertSeverity = () => {
    if (!isValidEmail(selectedPatient?.email)) {
      return 'error';
    }

    switch (true) {
      case noCheckbox || isPatientNotifEnabled:
        return selectedPatient?.email ? 'info' : 'error';
      default:
        return selectedPatient?.email ? 'warning' : 'info';
    }
  };

  return (
    <StepTemplate
      label={i18n._('Create the appointment')}
      isLocked={isLocked}
      onEditClick={() => {}} // Not needed, this is the last step in all scenarios
      isStepCompleted={false} // Can be safely always false for the moment.
      editText='-'
      {...otherProps}
    >
      <form onSubmit={handleSubmit}>
        {!noCheckbox && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isPatientNotifEnabled}
                onChange={(event) => {
                  handlePatientNotifChange(event.target.checked);
                }}
              />
            }
            label={i18n._('Notify the patient')}
            disabled={!selectedPatient?.email && !isPatientNotifEnabled}
          />
        )}
        {!isUnrestrictedMode &&
          <>
            {!isPatientForm &&
              <Alert severity={getAlertSeverity()}>
                <div>
                  {patientNotificationString}
                </div>
                <div>
                  {appointmentHasPayment && !isValidEmail(selectedPatient?.email) &&
                      i18n._('A valid email address is required for appointments with payment.')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '15px' }}>
                  {selectedPatient?.email && <Typography color='text.primary' sx={{ fontSize: '14px' }}>{selectedPatient?.email}</Typography>}
                  <Button variant='contained' color='inherit' className='appointmentForm-sideButton' onClick={handleAddContactInfoClick} size='small'>
                    {selectedPatient?.email ? i18n._('Edit email address') : i18n._('Add email address')}
                  </Button>
                </div>
              </Alert>}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isThirdPartyNotifEnabled}
                  onChange={(event) => handleThirdPartyCheckBoxChanged(event.target.checked)}
                />
                }
              label={i18n._('Notify external participants')}
            />
          </>}
        <Collapse
          in={isThirdPartyNotifEnabled}
        >
          <ChipInput
            value={thirdPartyEmailList}
            label={i18n._('Email addresses')}
            onChange={handleThirdPartyEmailListChange}
            onBeforeAdd={() => validateEmail(emailInputRef?.current)}
            onInputChange={handleThirdPartyEmailInputChange}
            inputValue={thirdPartyEmailAddressValue}
            TextFieldProps={{
              type: 'email',
              helperText: emailErrorText || i18n._('Press "Enter" to validate email addresses.'),
              FormHelperTextProps: { error: Boolean(emailErrorText) || Boolean(thirdPartyEmailAddressValue) },
            }}
            inputRef={emailInputRef}
          />
        </Collapse>
        <div className='appointmentForm-line'>
          <TextField
            className='contactInput'
            id='reason'
            label={i18n._('Reason (Required)')}
            value={reason}
            onChange={(event) => dispatch(updateReason({ reason: event.target.value }))}
            variant='outlined'
            fullWidth
            autoFocus
            inputProps={{ maxLength: 50 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <Typography variant='caption' color={reason.length > 40 ? 'error' : 'textSecondary'}>
                    {reason.length}/50
                  </Typography>
                </InputAdornment>
              ),
            }}
            error={reason.length > 50}
          />
        </div>
        <div className='appointmentForm-line'>
          <Button
            variant='contained'
            type='submit'
            disabled={
                (appointmentHasPayment && !isValidEmail(selectedPatient?.email)) ||
                !reason ||
                reason.length > 50 ||
                (isThirdPartyNotifEnabled && Boolean(thirdPartyEmailAddressValue)) ||
                hasPatientMissingFields ||
                expertMissingRpps
            }
          >
            {i18n._('Finish')}
          </Button>
        </div>
      </form>
    </StepTemplate>
  );
};
